import React from 'react';
import PropTypes from 'prop-types';

const DEBOUNCE_MS = 300;

export function buildPreviewSrc(previewUrl, {
  style, size, templateId, logoSignedId, logoRemoved, transparency, subtext, urlType,
} = {}) {
  const params = new URLSearchParams();
  // The download page previews either the short or the long URL, and the endpoint
  // reads url_type to decide which (LinkQrcode#url_type). Absent on the template
  // styling form, which has no link to encode.
  if (urlType) {
    params.set('url_type', urlType);
  }
  // The template styling form always sends a style (it is previewing an unsaved one).
  // The link download preview never does — that endpoint resolves the saved template
  // server-side — so an absent style must not become the string "undefined".
  if (style) {
    params.set('style', JSON.stringify(style));
  }
  params.set('size', String(size));
  // A picked (unsaved) logo is the source when present; the saved-template logo
  // (via qr_code_template_id) is only used when nothing new was picked AND the
  // saved one is not marked for removal. Dropping the id is what makes the preview
  // show a plain QR before the removal is actually saved — the endpoint reads that
  // param for no other purpose.
  if (logoSignedId) {
    params.set('logo_signed_id', logoSignedId);
  } else if (templateId != null && !logoRemoved) {
    params.set('qr_code_template_id', String(templateId));
  }
  // Sent explicitly in both directions, never omitted-to-mean-false: an absent
  // transparency param means "use the template's stored styling" on the server
  // (Terminus::QrCode::Download#transparency_override), so a preview that omitted it
  // when unchecked would disagree with the file the user downloads.
  if (transparency != null) {
    params.set('transparency', transparency ? '1' : '0');
  }
  if (subtext) {
    params.set('subtext', subtext);
  }
  return `${previewUrl}?${params.toString()}`;
}

// The subset of props that determine the rendered image. Kept in one place so the
// constructor, the change comparison, and the rebuild cannot drift apart.
function srcOptions(props) {
  const {
    style, size, templateId, logoSignedId, logoRemoved, transparency, subtext, urlType,
  } = props;
  return {
    style, size, templateId, logoSignedId, logoRemoved, transparency, subtext, urlType,
  };
}

class QrPreview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // The download page server-renders an accurate first image; using it avoids both
      // a spinner on load and a redundant request for something already rendered.
      src: props.initialSrc || buildPreviewSrc(props.previewUrl, srcOptions(props)),
      loading: false,
      failed: false,
    };
    this.timer = null;
  }

  componentDidUpdate(prev) {
    // Compared through srcOptions rather than prop by prop, so adding an option to
    // the URL cannot silently fail to trigger a refresh. `style` is compared by
    // reference, as it always was -- QrStyleForm rebuilds it on every edit.
    const before = srcOptions(prev);
    const after = srcOptions(this.props);
    if (Object.keys(after).every((key) => before[key] === after[key])) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.setState({
        src: buildPreviewSrc(this.props.previewUrl, srcOptions(this.props)),
        loading: true,
        failed: false,
      });
    }, DEBOUNCE_MS);
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  render() {
    const { src, loading, failed } = this.state;
    const { uploadingLogo, errorMessage } = this.props;
    const busy = loading || uploadingLogo;
    return (
      <div className="qr-preview">
        <div className="qr-section-label">Preview</div>
        <div className="qr-preview-frame" data-testid="preview-panel">
          <img
            className="qr-preview-img"
            data-testid="qr-preview-img"
            src={src}
            alt="QR code preview"
            style={{ opacity: busy ? 0.5 : 1 }}
            onLoad={() => this.setState({ loading: false, failed: false })}
            onError={() => this.setState({ loading: false, failed: true })}
          />
          {busy && <span className="qr-preview-spinner" data-testid="preview-spinner" />}
        </div>
        {failed && (
          <p className="qr-preview-note" data-testid="preview-error">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
}

QrPreview.propTypes = {
  previewUrl: PropTypes.string.isRequired,
  style: PropTypes.object,
  size: PropTypes.number.isRequired,
  templateId: PropTypes.number,
  logoSignedId: PropTypes.string,
  uploadingLogo: PropTypes.bool,
  logoRemoved: PropTypes.bool,
  transparency: PropTypes.bool,
  subtext: PropTypes.string,
  urlType: PropTypes.string,
  initialSrc: PropTypes.string,
  errorMessage: PropTypes.string,
};

QrPreview.defaultProps = {
  style: null,
  templateId: null,
  logoSignedId: null,
  uploadingLogo: false,
  logoRemoved: false,
  transparency: null,
  subtext: '',
  urlType: null,
  initialSrc: null,
  // The template styling form's wording (#2504): its fields highlight inline, so the
  // note points at them. Callers with nothing to highlight -- the link download form --
  // pass their own.
  errorMessage: 'Preview will update once the highlighted fields are fixed.',
};

export default QrPreview;
